/*
 * AI 问照片「内测申请」档位商品配置。
 *
 * 卖点 = 预付费购买「可识别照片张数」额度,内测专享价。
 * 这里配前端展示 + 下单要用的 goodsID;实际支付金额以订单系统的商品价为准。
 *
 * goodsID 按环境拆分(2026-06-10 拿到 prod 正式 ID 后):
 *   - staging(预发)/ prod : 连线上后端,用正式商品价格 ID(¥50 / ¥100 / ¥300 / ¥1000,见 GOODS_ID_PROD)
 *   - test / dev : 连测试后端,用 ¥0.01 压测商品 ID(见 GOODS_ID_TEST)
 * 由 __BUILD_ENV__ 在构建时区分。默认走正式 ID,只有 test / dev 切测试 ID ——
 * 预发连的是线上订单库,测试 ID 在线上库不存在,创建订单会报「商品不存在 / id 不对」,
 * 所以预发必须跟线上一致用正式 ID(2026-06-10 预发复用方反馈)。
 */

/** webpack DefinePlugin 注入的构建环境标识(prod / staging / test / dev) */
declare const __BUILD_ENV__: string

/** 内测申请的产品维度标识,与后端 beta/quota、beta/order 的 product_key 对齐 */
export const AI_PHOTO_BETA_PRODUCT_KEY = 'ai_photo_beta'

/**
 * prod 正式商品价格 ID(2026-06-10 张璇在「其他单品」配置)。
 * 价格: 5000→¥50 / 1万→¥100 / 3万→¥300 / 10万→¥1000。
 */
const GOODS_ID_PROD: Record<string, string> = {
  '5000': '2064553979379933184',
  '10000': '2064554141933506560',
  // 30000 档原 ID(…2818560)误配成 ¥150 且不便改价,2026-06-10 14:04 重建为 ¥300
  '30000': '2064589428076843008',
  '100000': '2064554442057801728'
}

/** test / staging / dev 测试商品价格 ID(¥0.01 压测价,2026-06-08 配置) */
const GOODS_ID_TEST: Record<string, string> = {
  '5000': '2063978831035404288',
  '10000': '2063979008542543872',
  '30000': '2063979166202236928',
  '100000': '2063979253586366464'
}

/**
 * 按构建环境选商品 ID 集:默认走正式 ID,只有 test / dev 切测试 ID。
 *   - test / dev : 连测试后端 → 测试商品 ID
 *   - staging(预发)/ prod 及其它 : 连线上后端 → 正式商品 ID
 * 预发必须用正式 ID:它连线上订单库,测试 ID 在线上不存在会「商品不存在」。
 */
const GOODS_ID_MAP: Record<string, string> =
  __BUILD_ENV__ === 'test' || __BUILD_ENV__ === 'dev'
    ? GOODS_ID_TEST
    : GOODS_ID_PROD

/** 单个档位配置 */
export interface AiPhotoBetaPlan {
  /** 档位标识(选中态 / 埋点用) */
  key: string
  /** 订单系统商品价格 ID(下单入参 goodsID) */
  goodsID: string
  /** 购买的识别额度(张数) */
  quota: number
  /** 内测专享价(元)。= prod 后台真实价,所见即所付;test 实付为 ¥0.01 */
  price: number
  /** 划线原价(元) */
  originPrice: number
  /** 时长估算文案,如「≈半年」「试用」 */
  durationLabel: string
  /** 角标文案,如「推荐」;空串不展示 */
  tagLabel: string
}

/**
 * 4 个档位。price = prod 后台真实价(所见即所付),统一 1 分/张、原价按 2 折划线。
 * goodsID 由 GOODS_ID_MAP 按环境注入。
 */
export const AI_PHOTO_BETA_PLANS: AiPhotoBetaPlan[] = [
  {
    key: '5000',
    goodsID: GOODS_ID_MAP['5000'],
    quota: 5000,
    price: 50,
    originPrice: 250,
    durationLabel: '试用',
    tagLabel: ''
  },
  {
    key: '10000',
    goodsID: GOODS_ID_MAP['10000'],
    quota: 10000,
    price: 100,
    originPrice: 500,
    durationLabel: '≈半年',
    tagLabel: '推荐'
  },
  {
    key: '30000',
    goodsID: GOODS_ID_MAP['30000'],
    quota: 30000,
    price: 300,
    originPrice: 1500,
    durationLabel: '≈1年',
    tagLabel: ''
  },
  {
    key: '100000',
    goodsID: GOODS_ID_MAP['100000'],
    quota: 100000,
    price: 1000,
    originPrice: 5000,
    durationLabel: '≈3年',
    tagLabel: ''
  }
]

/** 默认选中「≈半年」(1万)档,与设计稿一致 */
export const AI_PHOTO_BETA_DEFAULT_PLAN_KEY = '10000'

/** 专享价大卡下方的权益列表 */
export const AI_PHOTO_BETA_BENEFITS: string[] = [
  '1 分钱 = 1 张照片',
  '享受 5 倍优惠 · 仅内测期',
  '永久有效 · 不限期使用',
  '客户经理 1V1 服务 · 7 天内开通'
]

/** 按 key 取档位配置;找不到返回 undefined */
export function getAiPhotoBetaPlan(key: string): AiPhotoBetaPlan | undefined {
  return AI_PHOTO_BETA_PLANS.find((p) => p.key === key)
}
